import { useState } from "react";
import {
  Plus,
  Loader2,
  Package,
  ChevronDown,
  Columns3,
  Layers,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterMenu, type FilterMenuOption } from "@/components/ui/filter-menu";
import { useServicesByCategory } from "@/hooks/useManageServices";
import { AddCategoryDialog } from "./dialogs/AddCategoryDialog";
import { ServiceTableRow } from "./shared/ServiceTableRow";
import {
  SERVICE_COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  type ServiceColumnKey,
} from "./shared/serviceColumns";
import type { ServiceCategory } from "@/types/services";

interface ManageServicesListProps {
  categories: ServiceCategory[];
  onAddNew: () => void;
  onView: (serviceId: string) => void;
  canManageServices: boolean;
}

export const ManageServicesList = ({
  categories,
  onAddNew,
  onView,
  canManageServices,
}: ManageServicesListProps) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<ServiceColumnKey>>(
    () => new Set(DEFAULT_VISIBLE_COLUMNS)
  );

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const toggleColumn = (key: ServiceColumnKey) => {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        // Always keep at least one optional column visible alongside the name.
        if (next.size === 1) return current;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const categoryOptions: FilterMenuOption[] = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const {
    data: services,
    isLoading,
    isError,
  } = useServicesByCategory(selectedCategoryIds, true);

  return (
    <div className="flex h-full flex-col overflow-hidden pr-1">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Header — merged with the table surface */}
        <CardHeader className="gap-4 border-b border-border bg-card p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="z-h1 text-foreground">
                Manage Services
              </CardTitle>
              <CardDescription className="z-secondary text-muted-foreground">
                Create and edit services available to staff and patients.
              </CardDescription>
            </div>
            {canManageServices && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button id="manage-services-add-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Add
                    <ChevronDown className="ml-2 h-4 w-4 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="z-eyebrow text-muted-foreground">
                    Create new
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    id="manage-services-add-service"
                    onSelect={onAddNew}
                  >
                    <Briefcase className="mr-2 h-4 w-4" />
                    New service
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    id="manage-services-add-category"
                    onSelect={() => setIsAddCategoryOpen(true)}
                  >
                    <Layers className="mr-2 h-4 w-4" />
                    New category
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Toolbar — department filter + column visibility */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <FilterMenu
              label="Department"
              options={categoryOptions}
              value={selectedCategoryIds}
              onValueChange={toggleCategory}
              multiple
              emptyText="No departments found"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id="manage-services-columns-btn"
                  variant="outline"
                  size="sm"
                  className="h-9"
                >
                  <Columns3 className="mr-1.5 h-4 w-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="z-eyebrow text-muted-foreground">
                  Show columns
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SERVICE_COLUMNS.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={visibleColumns.has(column.key)}
                    onCheckedChange={() => toggleColumn(column.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        {/* Table */}
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : isError ? (
              <EmptyState
                title="Failed to load services"
                description="Please try again in a moment."
              />
            ) : !services || services.length === 0 ? (
              <EmptyState
                title="No services found"
                description="Add your first service for this department."
              />
            ) : (
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-muted/40 backdrop-blur">
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <TableHead className="z-eyebrow w-[360px] text-muted-foreground">
                      Service
                    </TableHead>
                    {SERVICE_COLUMNS.map((column) =>
                      visibleColumns.has(column.key) ? (
                        <TableHead
                          key={column.key}
                          className="z-eyebrow text-muted-foreground"
                        >
                          {column.label}
                        </TableHead>
                      ) : null
                    )}
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((service) => (
                    <ServiceTableRow
                      key={service.id}
                      service={service}
                      categoryName={
                        service.category_name ?? service.category ?? "Unknown"
                      }
                      visibleColumns={visibleColumns}
                      onView={() => onView(service.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {canManageServices && (
        <AddCategoryDialog
          open={isAddCategoryOpen}
          onOpenChange={setIsAddCategoryOpen}
        />
      )}
    </div>
  );
};

const EmptyState = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
    <Package className="h-10 w-10 opacity-40" />
    <p className="z-body-md text-foreground">{title}</p>
    <p className="z-secondary">{description}</p>
  </div>
);
